/**
 * Client HTTP de l'API : jeton d'accès en mémoire, rafraîchissement unique
 * (single-flight) via le cookie httpOnly, erreurs typées.
 */
import type { ApiError as ApiErrorBody, AuthResponse } from '@zakat/shared';
import { useAuthStore } from '@/stores/auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isOffline(): boolean {
    return this.status === 0;
  }
}

export const API_BASE = '/api';

let refreshing: Promise<string | null> | null = null;

/** Rafraîchit la session une seule fois même si plusieurs requêtes échouent en même temps. */
export function refreshSession(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return null;
      }
      const body = (await res.json()) as AuthResponse;
      useAuthStore.getState().setSession(body.accessToken, body.user, body.expiresInSeconds);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

/** Jeton valide (rafraîchi s'il expire dans moins de 30 s). */
export async function getAccessToken(): Promise<string | null> {
  const { accessToken, expiresAt } = useAuthStore.getState();
  if (accessToken && expiresAt - Date.now() > 30_000) return accessToken;
  if (!useAuthStore.getState().user && !accessToken) return null;
  return refreshSession();
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Ne pas joindre le jeton (routes publiques). */
  anonymous?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, anonymous, query, headers, ...init } = options;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  const doFetch = async (token: string | null) => {
    const h = new Headers(headers);
    if (body !== undefined && !isForm) h.set('Content-Type', 'application/json');
    if (token) h.set('Authorization', `Bearer ${token}`);
    return fetch(buildUrl(path, query), {
      ...init,
      headers: h,
      credentials: 'include',
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  };

  let res: Response;
  try {
    const token = anonymous ? null : await getAccessToken();
    res = await doFetch(token);
    if (res.status === 401 && !anonymous && useAuthStore.getState().user) {
      const fresh = await refreshSession();
      if (fresh) res = await doFetch(fresh);
    }
  } catch {
    throw new ApiError(0, 'OFFLINE', 'Pas de connexion internet. Réessayez dès que le réseau revient.');
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok) {
    const err = (contentType.includes('json') ? await res.json().catch(() => null) : null) as ApiErrorBody | null;
    throw new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? `Erreur ${res.status}`, err?.details);
  }
  if (contentType.includes('json')) return (await res.json()) as T;
  return (await res.blob()) as unknown as T;
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => api<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => api<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => api<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => api<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => api<T>(path, { ...options, method: 'DELETE' }),
};

/** Message d'erreur lisible pour l'utilisateur. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Une erreur est survenue';
}
